// OBT F3-C — Seçili kampanya analitiği: özet + cevap dağılımı + süreler + karar.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCampaignSummary,
  fetchResponseSummary,
  fetchReviewDurations,
  fetchDecisionSetHistory,
} from '../reportApi';
import { normalizeObtError } from '../../obtErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

export function useCampaignAnalytics(campaignId, { enabled = true } = {}) {
  const [state, setState] = useState({
    loading: Boolean(campaignId) && enabled,
    error: null,
    summary: null,
    responseSummary: null,
    durations: null,
    decisionHistory: null,
  });
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (!campaignId || !enabled) {
      setState({
        loading: false, error: null, summary: null,
        responseSummary: null, durations: null, decisionHistory: null,
      });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const opt = { signal: controller.signal };
    try {
      const [summary, responseSummary, durations, decisionHistory] = await Promise.all([
        fetchCampaignSummary(campaignId, opt),
        fetchResponseSummary(campaignId, opt),
        fetchReviewDurations(campaignId, opt),
        fetchDecisionSetHistory(campaignId, opt).catch(() => null),
      ]);
      if (controller.signal.aborted) return;
      setState({
        loading: false, error: null, summary, responseSummary, durations, decisionHistory,
      });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({
        loading: false, error: normalizeObtError(error), summary: null,
        responseSummary: null, durations: null, decisionHistory: null,
      });
    }
  }, [campaignId, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  return { ...state, reload: load };
}
